'use client';

import {
  type AddressFormInputValidation,
  AddressFormV3,
  type AddressFormV3Props,
  FieldName,
  mergeValidationConfigs,
} from '@jouzen/ecom-components/v3/AddressForm';
import { type FormControlChangeEvent } from '@jouzen/ecom-components/v3/FormControl';
import { cx } from 'class-variance-authority';
import merge from 'lodash/merge';
import noop from 'lodash/noop';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { type CountryData } from '@/app/hooks/useCountryData';
import useFocusFirstErrorComponent from '@/app/hooks/useFocusFirstErrorComponent';
import { logger as browserLogger } from '@/logger/logger.browser';
import { type AddressFormValidation } from '@/queries/types/graphql';

import { transformValidationData } from './utils';

export interface AddressFormProps extends Omit<
  AddressFormV3Props,
  'className'
> {
  readonly addressFormValidation: AddressFormValidation;
  /**
   * Optional content rendered inside the underlying <form>, immediately
   * before the submit button. Useful for adding an extra field
   * (e.g. an "I agree" checkbox) whose value is managed externally and
   * combined with the address inside `onFormSubmit`.
   *
   * The node is portaled into a host <div> we splice into AddressFormV3's
   * form, so it inherits the form's flex-column gap spacing and
   * participates in native form behavior (Tab order, Enter-to-submit).
   */
  readonly beforeSubmit?: ReactNode;
  readonly className?: string; // used on this wrapper element
  readonly countryData: CountryData;
  readonly countryTooltip?: ReactNode; // rendered next to the Country field label
  readonly formClassName?: AddressFormV3Props['className']; // passed down to form
  readonly loading?: boolean;
  readonly setSelectedCountryCode: Dispatch<SetStateAction<string>>;
}

const AddressForm = ({
  addressFormValidation,
  beforeSubmit,
  className = '',
  countryData,
  countryTooltip,
  excludedFields: initExcludedFields = [],
  fieldProps: fieldPropsOverrides = {},
  formClassName = '',
  hiddenFields = [],
  initialValues = {},
  inputValidation: inputValidationOverrides,
  loading: isLoading = false,
  selectedCountryCode,
  setSelectedCountryCode,
  submitButton,
  onFormSubmit = noop,
  onValidationError = noop,
  ...formProps
}: AddressFormProps): JSX.Element => {
  const formUuid = useId();
  const formId = formProps.id ?? `address-form-${formUuid}`;

  // focus on first error, on validation
  const formRef = useRef<HTMLDivElement>(null);
  const checkoutFocusFirstErrorComponent = useFocusFirstErrorComponent(formRef);

  // Portal target: the country label element rendered by AddressFormV3
  const [countryLabelEl, setCountryLabelEl] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!countryTooltip) return;
    const countrySelect = formRef.current?.querySelector<HTMLSelectElement>(
      'select[name="country"]',
    );
    const label =
      countrySelect && formRef.current
        ? formRef.current.querySelector<HTMLElement>(
            `label[for="${countrySelect.id}"]`,
          )
        : null;
    if (label) {
      // Make the label a flex container so the tooltip sits inline next to the
      // label text (the inner Typography span has display:block which would
      // otherwise push the portal content onto a new line).
      label.style.display = 'flex';
      label.style.alignItems = 'center';
      label.style.gap = '0.25rem';
    }
    setCountryLabelEl(label);
  }, [countryTooltip, isLoading]);

  // Portal target: a host <div> spliced into AddressFormV3's form, sitting
  // immediately before the submit button. The form is a flex-column with
  // gap-y-4, so the host inherits the same spacing as the fieldset above
  // and the submit button below.
  const [beforeSubmitEl, setBeforeSubmitEl] = useState<HTMLElement | null>(
    null,
  );
  useEffect(() => {
    if (!beforeSubmit) return;
    const formEl = formRef.current?.querySelector<HTMLFormElement>('form');
    const submitBtn = formEl?.querySelector<HTMLButtonElement>(
      'button[type="submit"]',
    );
    if (!formEl || !submitBtn) return;

    // Reuse an existing host if AddressFormV3 hasn't remounted; otherwise
    // create one. The data attribute also defends against StrictMode's
    // double-invocation of effects in dev briefly creating two hosts.
    let host = formEl.querySelector<HTMLDivElement>(
      ':scope > [data-eop-before-submit]',
    );
    if (!host) {
      host = document.createElement('div');
      host.setAttribute('data-eop-before-submit', '');
      formEl.insertBefore(host, submitBtn);
    }
    setBeforeSubmitEl(host);

    return () => {
      host?.remove();
      setBeforeSubmitEl(null);
    };
    // isLoading is included because AddressFormV3 fully re-renders once
    // validation/country data settle, mirroring the countryTooltip effect.
  }, [beforeSubmit, isLoading]);

  const excludedFields = useMemo(
    () =>
      countryData.hasRegions
        ? initExcludedFields
        : [...initExcludedFields, FieldName.State],
    [countryData.hasRegions, initExcludedFields],
  );

  const fieldProps = useMemo(
    () =>
      merge({}, fieldPropsOverrides, {
        [FieldName.State]: {
          options: countryData.regionOptions,
        },
        [FieldName.Country]: {
          onChange: (evt: FormControlChangeEvent) => {
            fieldPropsOverrides[FieldName.Country]?.onChange?.(evt);
            setSelectedCountryCode(evt.target.value);
          },
          options: countryData.countryOptions,
        },
        [FieldName.Attention]: {
          clickToShow: false,
        },
      }),
    [
      countryData.countryOptions,
      countryData.regionOptions,
      fieldPropsOverrides,
      setSelectedCountryCode,
    ],
  );

  const inputValidation = useMemo(() => {
    const countryValidation: AddressFormInputValidation =
      transformValidationData(addressFormValidation);

    // merge country data from server with overrides, using our custom fn
    const config: AddressFormInputValidation = mergeValidationConfigs(
      countryValidation,
      inputValidationOverrides as unknown as Partial<AddressFormInputValidation>,
    );

    return config;
  }, [addressFormValidation, inputValidationOverrides]);

  const handleValidationError: AddressFormV3Props['onValidationError'] = (
    errors,
    values,
  ) => {
    // Fire callback
    onValidationError(errors, values);

    // Focus on first invalid field
    checkoutFocusFirstErrorComponent();
  };

  const handleInvalidFields = (invalidFields: FieldName[]) => {
    browserLogger.warn('All hidden fields must provide valid initialValues', {
      invalidFields,
    });
  };

  return (
    <div ref={formRef} className={cx(className)}>
      {countryTooltip &&
        countryLabelEl &&
        createPortal(countryTooltip, countryLabelEl)}
      {beforeSubmit &&
        beforeSubmitEl &&
        createPortal(beforeSubmit, beforeSubmitEl)}
      <AddressFormV3
        key={formId} // updating key forces re-render of form, forcing an update of initialValues
        id={formId}
        noValidate
        {...formProps}
        className={formClassName}
        excludedFields={excludedFields}
        fieldProps={fieldProps}
        hiddenFields={hiddenFields}
        initialValues={{
          ...initialValues,
          country: selectedCountryCode,
        }}
        optionalFields={[FieldName.Attention]}
        inputValidation={inputValidation}
        loading={isLoading}
        onFormSubmit={onFormSubmit}
        onInvalidFields={handleInvalidFields}
        onValidationError={handleValidationError}
        selectedCountryCode={selectedCountryCode}
        submitButton={submitButton}
      />
    </div>
  );
};

export default AddressForm;
